import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, vModelText as _vModelText, normalizeStyle as _normalizeStyle, withDirectives as _withDirectives, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = { class: "cx-combobox__chips" }
const _hoisted_2 = ["placeholder", "size", "disabled"]
const _hoisted_3 = { class: "cx-combobox__autocomplete-chips" }
const _hoisted_4 = ["size", "disabled"]
const _hoisted_5 = { class: "cx-combobox__icons" }
const _hoisted_6 = {
  key: 0,
  class: "cx-combobox__reset-icon"
}
const _hoisted_7 = { class: "cx-combobox__append-icon" }
const _hoisted_8 = {
  key: 2,
  class: "cx-combobox__reset"
}

import { ref, computed, onMounted, onUpdated, ComputedRef } from 'vue';
import { doesStringIncludeString } from '@/utils/stringOperations';
import listNavHelper from '@/utils/listnavigationhelper';
import CxChip from './CxChip.vue';
import CxOptions from './CxOptions.vue';
import CxResetButton from '@/components/CxResetButton.vue';
import CxPopper from '@/components/CxPopper.vue';
import popperOptions from './CxOptions.popper';
import CxSpinner from '@/components/CxSpinner.vue';
import CxIcon from '@/components/CxIcon.vue';
import { gettext } from '@/translations/gettext.setup';

interface Item {
  id: string,
  name: string,
  subtitle?: string
}

enum Variant {
  Combobox = 'combobox',
  Autocomplete = 'autocomplete'
}

interface Props {
  items: Item[],
  value: string[],
  input: (value: string[]) => void,
  multiselect?: boolean,
  label?: String,
  isChips?: boolean,
  isDropdown?: boolean,
  isTextInput?: boolean,
  isSubtitle?: boolean,
  isIcon?: boolean,
  isTooltip?: boolean,
  disabled?: boolean,
  loading?: boolean,
  placeholder?: string,
  variant?: `${Variant}`
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CxCombobox',
  props: {
    items: {},
    value: {},
    input: {},
    multiselect: { type: Boolean, default: true },
    label: {},
    isChips: { type: Boolean },
    isDropdown: { type: Boolean },
    isTextInput: { type: Boolean },
    isSubtitle: { type: Boolean },
    isIcon: { type: Boolean },
    isTooltip: { type: Boolean },
    disabled: { type: Boolean },
    loading: { type: Boolean },
    placeholder: {},
    variant: { default: Variant.Combobox }
  },
  setup(__props: any) {

const props = __props;

const { $gettext } = gettext;

const textInput = ref('');
const isDropdownVisible = ref(false);
const activeOption = ref<Item | null>(null);
const popper = ref<any>(null);

// Template Refs
const container = ref<HTMLElement | null>(null);
const popperRef = ref<InstanceType<typeof CxPopper> | null>(null);

const placeholderText = computed(() => {
  if (!props.isTextInput) return '';
  if (props.placeholder) return props.placeholder;
  const prefix = $gettext('Add');
  return `${prefix} ${props.label}`;
});

const applyStyle = computed(() => {
  const labelLengthWidthMultiplier = 7;
  return {
    input: {
      width: textInput.value.length ? 'auto' : `${placeholderText.value.length * labelLengthWidthMultiplier}px`,
      maxWidth: textInput.value.length && container.value ? `${container.value.clientWidth}px` : 'auto'
    }
  };
});

const applyClass = computed(() => {
  return {
    container: {
      'cx-combobox__chips-container--interactive': props.isTextInput,
      'cx-combobox__chips-container--multiselect': props.multiselect,
      'cx-combobox__chips-container--higher': props.isSubtitle && props.variant !== Variant.Autocomplete,
      'cx-combobox__chips-container--loading': props.loading,
      'cx-combobox__chips-container--disabled': props.disabled,
    },
    input: {
      'cx-combobox__input--disabled': props.disabled,
      'cx-combobox__input--loading': props.loading
    }
  };
});

const options = computed(() => {
  return props.items.filter(i => doesStringIncludeString(i.name, textInput.value));
});

const isSelected = (item: Item) => {
  return props.value.includes(item.id);
};

const chips = computed(() => {
  if (props.isChips) return props.items.filter(i => isSelected(i));
  return [];
});

const showInput = computed(() => {
  const isEmptySingleSelect = !props.multiselect && !chips.value.length;
  return props.isTextInput && (isEmptySingleSelect || props.multiselect);
});

const autocompleteChipText = (index: number, chip: Item) => {
  return index === chips.value.length - 1 ? chip.name : `${chip.name},`;
};

const labelStyle = computed(() => {
  return chips.value.length > 0 || isDropdownVisible.value || textInput.value.length ? 'cx-combobox__label--outside' : '';
});

const hideDropdown = () => {
  isDropdownVisible.value = false;
  activeOption.value = null;
};

const chipsSize = computed(() => {
  const size = chips.value.length;
  if (!size) hideDropdown();
  return size;
});

/**
  * Updates the list of selected chips/dropdown options basing on the ongoing selection or deselection of a single chip/dropdown option.
*/
const onToggle = (item) => {
  let ids;
  if (props.multiselect) {
    ids = isSelected(item) ? props.value.filter(i => i !== item.id) : [...props.value, item.id];
  } else {
    ids = isSelected(item) ? props.value.filter(i => i !== item.id) : [item.id];
    textInput.value = '';
  }
  props.input(ids);
};

const onReset = () => {
  props.input([]);
  textInput.value = '';
  hideDropdown();
};

const showDropdown = () => {
  if (props.isDropdown && !isDropdownVisible.value) isDropdownVisible.value = true;
};

const onInput = (e) => {
  switch (e.key) {
    case 'Enter': {
      if (!isDropdownVisible.value) showDropdown();
      if (!activeOption.value && options.value.length) {
        const [firstOption] = options.value;
        onToggle({ id: firstOption.id, isChipSelected: !isSelected(firstOption) });
        activeOption.value = firstOption;
      } else {
        const item = options.value.find(i => activeOption.value && i.id === activeOption.value.id);
        if (item) {
          const { id } = item;
          onToggle({ id, isChipSelected: !isSelected(item) });
        }
      }
      break;
    }
    case 'Escape': {
      hideDropdown();
      break;
    }
    case 'Backspace': {
      if (chipsSize.value && (!textInput.value.length || !e.target.selectionStart)) {
        const item = chips.value[chipsSize.value - 1];
        onToggle({ id: item.id, isChipSelected: !isSelected(item) });
      }
      break;
    }
    case 'ArrowUp':
    case 'ArrowDown': {
      e.preventDefault();
      if (!isDropdownVisible.value) showDropdown();
      activeOption.value = listNavHelper.getActiveItem(options.value, activeOption.value, e.key);
      break;
    }
    default: {
      showDropdown();
      break;
    }
  }
};

const onClickOutside = () => {
  hideDropdown();
};

onMounted(() => {
  if (props.isDropdown) {
    popper.value = {
      reference: container.value,
      options: popperOptions
    };
  }
});

onUpdated(() => {
  if (props.isDropdown && !props.disabled && !props.loading && popperRef.value) popperRef.value.updatePopper();
});

// Directive
const VFocus = (el, binding) => {
  if (binding.value) el.focus();
};


return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["cx-combobox", _ctx.variant ? `cx-combobox--${_ctx.variant}` : ''])
  }, [
    (_ctx.variant === Variant.Autocomplete && _ctx.label)
      ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          class: _normalizeClass(["cx-combobox__label", labelStyle.value])
        }, _toDisplayString(_ctx.label), 3))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      (_ctx.variant !== Variant.Autocomplete)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["cx-combobox__chips-container", applyClass.value.container]),
            ref_key: "container",
            ref: container,
            onClick: showDropdown
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chips.value, (chip, index) => {
              return (_openBlock(), _createBlock(CxChip, {
                item: chip,
                isIcon: _ctx.isIcon,
                isSubtitle: _ctx.isSubtitle,
                isTooltip: _ctx.isTooltip,
                key: index,
                input: onToggle,
                disabled: _ctx.disabled,
                loading: _ctx.loading
              }, null, 8, ["item", "isIcon", "isSubtitle", "isTooltip", "disabled", "loading"]))
            }), 128)),
            (_ctx.isTextInput)
              ? _withDirectives((_openBlock(), _createElementBlock("input", {
                  key: 0,
                  class: _normalizeClass(["cx-combobox__input", applyClass.value.input]),
                  type: "text",
                  placeholder: placeholderText.value,
                  style: _normalizeStyle(applyStyle.value.input),
                  size: textInput.value.length || 1,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((textInput).value = $event)),
                  disabled: _ctx.disabled || _ctx.loading,
                  onKeydown: onInput
                }, null, 46, _hoisted_2)), [
                  [_vModelText, textInput.value],
                  [VFocus, isDropdownVisible.value]
                ])
              : _createCommentVNode("", true),
            (_ctx.loading)
              ? (_openBlock(), _createBlock(CxSpinner, {
                  key: 1,
                  size: 15,
                  width: 1
                }))
              : _createCommentVNode("", true)
          ], 2))
        : _createCommentVNode("", true),
      (_ctx.variant === Variant.Autocomplete)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass(["cx-combobox__chips-container", applyClass.value.container]),
            ref_key: "container",
            ref: container,
            onClick: showDropdown
          }, [
            _createElementVNode("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chips.value, (chip, index) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: index,
                  class: _normalizeClass(["cx-combobox__chip", { 'cx-combobox__chip--singleselect': !_ctx.multiselect}])
                }, _toDisplayString(autocompleteChipText(index, chip)), 3))
              }), 128)),
              (showInput.value)
                ? _withDirectives((_openBlock(), _createElementBlock("input", {
                    key: 0,
                    class: _normalizeClass(["cx-combobox__input", applyClass.value.input]),
                    type: "text",
                    size: textInput.value.length || 1,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((textInput).value = $event)),
                    disabled: _ctx.disabled || _ctx.loading,
                    onKeydown: onInput
                  }, null, 42, _hoisted_4)), [
                    [_vModelText, textInput.value],
                    [VFocus, isDropdownVisible.value]
                  ])
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_5, [
              (chips.value.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createVNode(CxIcon, {
                      icon: "close",
                      size: 14,
                      onClick: onReset
                    })
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_7, [
                _createVNode(CxIcon, {
                  icon: "brightness_1",
                  size: 10
                })
              ])
            ]),
            (_ctx.loading)
              ? (_openBlock(), _createBlock(CxSpinner, {
                  key: 0,
                  size: 15,
                  width: 1
                }))
              : _createCommentVNode("", true)
          ], 2))
        : _createCommentVNode("", true),
      (_ctx.variant !== Variant.Autocomplete)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(CxResetButton, {
              disabled: (!chipsSize.value && !textInput.value.length) || _ctx.disabled || _ctx.loading,
              height: "42",
              onReset: onReset
            }, null, 8, ["disabled"])
          ]))
        : _createCommentVNode("", true)
    ]),
    (_ctx.isDropdown && popper.value && !_ctx.disabled && !_ctx.loading)
      ? (_openBlock(), _createBlock(CxPopper, {
          key: 1,
          reference: popper.value.reference,
          options: popper.value.options,
          ref_key: "popperRef",
          ref: popperRef
        }, {
          default: _withCtx(() => [
            (isDropdownVisible.value)
              ? (_openBlock(), _createBlock(CxOptions, {
                  key: 0,
                  items: options.value,
                  value: _ctx.value,
                  label: _ctx.label,
                  activeItem: activeOption.value,
                  input: onToggle
                }, null, 8, ["items", "value", "label", "activeItem"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["reference", "options"]))
      : _createCommentVNode("", true)
  ], 2)), [
    [_directive_click_outside, onClickOutside]
  ])
}
}

})